import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowUpToLine, Hourglass, ListOrdered, Pause, Play, TriangleAlert } from 'lucide-react';
import { apiClient } from '../../api/client';
import { useAccess } from '../../auth/access';
import { STACK_ACTIVITY_QUERY_KEY } from '../../hooks/useStackActivity';
import { duplicatesKey } from '../calibration/duplicateJob';
import { flatMeasureKey } from '../calibration/flatMeasureLabel';
import { frameSitesKey } from '../calibration/frameSitesKey';
import { rejectRemovalKey } from '../rejectRemovalJob';
import {
  groupActivity,
  lineLengths,
  queueText,
  tierOf,
  type ActivityControl,
  type ActivityItem,
  type ActivityPause,
  type ActivityQueue,
  type ActivityStatus,
  type ActivitySummary,
} from './activityItems';
import { JOBS_QUERY_KEY, useHeaderActivity, type ScanErrorNote } from './useHeaderActivity';
import './header.css';
import { TransferControls } from '../director/CollaborationTransfers';
import { TRANSFER_QUERY_KEY } from '../../api/transferTypes';
import { TS_SYNC_QUERY_KEY } from '../../api/tsSyncTypes';
import { BACKUP_STATUS_QUERY_KEY } from '../../api/backupTypes';
import type { TransferJob, TransferAction } from '../../api/transferTypes';

/** How long the list stays after the pointer leaves, so it can be reached. */
const HOVER_CLOSE_MS = 200;

function ProgressRing({ percent, idle }: { percent: number | null; idle: boolean }) {
  const radius = 7;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg
      className={`activity-ring${percent == null && !idle ? ' is-indeterminate' : ''}`}
      viewBox="0 0 18 18"
      width="18"
      height="18"
      aria-hidden="true"
    >
      <circle className="activity-ring-track" cx="9" cy="9" r={radius} />
      <circle
        className="activity-ring-fill"
        cx="9"
        cy="9"
        r={radius}
        strokeDasharray={circumference}
        // Nothing running: an empty ring, not a spinner caught mid-turn.
        strokeDashoffset={
          percent != null
            ? circumference * (1 - percent / 100)
            : idle
              ? circumference
              : circumference * 0.7
        }
      />
    </svg>
  );
}

type ActivityAction =
  | { type: 'transfer'; job: TransferJob; action: TransferAction }
  | { type: 'move'; control: ActivityControl; position: number }
  | { type: 'stop'; control: ActivityControl }
  | { type: 'pause'; pause: ActivityPause };

function runAction(action: ActivityAction): Promise<unknown> {
  if (action.type === 'transfer') return apiClient.collaborationWork(action.job.connection_id, { operation: 'transfer_control', id: action.job.id, revision: action.job.revision, action: action.action });
  if (action.type === 'pause') {
    const { dbId, job, paused } = action.pause;
    return paused ? apiClient.resumeJob(dbId, job) : apiClient.pauseJob(dbId, job);
  }
  const { control } = action;
  if (action.type === 'move') {
    if (control.kind === 'stack') return apiClient.moveStackJob(control.jobId, action.position);
    if (control.kind === 'wbpp-queued') return apiClient.moveQueuedWbppRun(control.queueId, action.position);
    return Promise.resolve();
  }
  if (control.kind === 'stack') return apiClient.cancelStackJob(control.jobId);
  if (control.kind === 'duplicates') return apiClient.stopCalibrationDuplicates(control.dbId);
  if (control.kind === 'rejects') return apiClient.stopRejectRemoval(control.dbId);
  if (control.kind === 'backup') return apiClient.stopBackup();
  if (control.kind === 'ts-sync') return apiClient.stopTsSync(control.jobId);
  if (control.kind === 'wbpp-running') return apiClient.cancelWbppRun(control.dbId);
  if (control.kind === 'preview') return apiClient.stopPreview(control.preview, control.key);
  if (control.kind === 'import') return apiClient.stopImport(control.dbId);
  if (control.kind === 'export') return apiClient.stopServerExport(control.dbId);
  if (control.kind === 'catalog-install') return apiClient.stopCatalogInstall();
  return apiClient.removeQueuedWbppRun(control.dbId, control.queueId);
}

interface RowControls {
  /** Whether this person may change this row's line. */
  allowed: (item: ActivityItem) => boolean;
  run: (action: ActivityAction) => void;
  busy: boolean;
  lengths: Record<ActivityQueue, number>;
  /** Close the list as a link takes the person away. */
  leave: () => void;
}

/** A scan's frame errors, with the way to its target and to dismiss it. */
function ScanErrorRow({
  note,
  onDismiss,
  onLeave,
}: {
  note: ScanErrorNote;
  onDismiss: (dbId: string) => void;
  onLeave: () => void;
}) {
  return (
    <div className="activity-error" role="note">
      <span className="activity-error-text">
        A quality scan finished with errors. {note.message}
        {note.href && (
          <>
            {' '}
            <Link to={note.href} className="activity-error-link" onClick={onLeave}>
              Open the sequence
            </Link>
          </>
        )}
      </span>
      <button
        type="button"
        className="activity-error-dismiss"
        aria-label={`Dismiss the quality scan errors in ${note.dbName}`}
        title="Dismiss"
        onClick={() => onDismiss(note.dbId)}
      >
        ✕
      </button>
    </div>
  );
}

/** The row's corner: its place in a line, waiting, or its progress. */
function stateText(item: ActivityItem): ReactNode {
  if (item.stateLabel) return item.stateLabel;
  if (item.status === 'attention') return 'needs attention';
  if (item.status === 'paused') return 'paused';
  if (item.status === 'queued') return queueText(item);
  if (item.status === 'waiting') {
    return (
      <>
        <Hourglass className="activity-count-icon" size={11} aria-hidden="true" />
        waiting
      </>
    );
  }
  return item.percent != null ? `${Math.round(item.percent)}%` : 'working';
}

const COUNT_ICONS = {
  attention: TriangleAlert,
  running: Play,
  queued: ListOrdered,
  waiting: Hourglass,
  paused: Pause,
} as const;

/** A count in words: `3 running`, `1 needs attention`. */
function countName(count: number, state: ActivityStatus): string {
  if (state === 'attention') return `${count} ${count === 1 ? 'needs' : 'need'} attention`;
  return `${count} ${state}`;
}

/** One icon and its count, named in words for a screen reader and a
 *  tooltip. Nothing when the count is zero. */
function JobCount({ count, state }: { count: number; state: ActivityStatus }) {
  if (count === 0) return null;
  const Icon = COUNT_ICONS[state];
  const name = countName(count, state);
  return (
    <span className="activity-count" data-job-state={state} title={name}>
      <Icon className="activity-count-icon" size={12} aria-hidden="true" />
      <span aria-hidden="true">{count}</span>
      <span className="activity-count-name">{name}</span>
    </span>
  );
}

/** How many jobs run, wait in a line, wait for their turn and need a
 *  person, as icons with counts, so the chip stays small. */
function JobCounts({ summary }: { summary: ActivitySummary }) {
  return (
    <span className="activity-counts">
      <JobCount count={summary.running} state="running" />
      <JobCount count={summary.queued} state="queued" />
      <JobCount count={summary.waiting} state="waiting" />
      <JobCount count={summary.paused} state="paused" />
      <JobCount count={summary.attention} state="attention" />
    </span>
  );
}

function ActivityRow({ item, controls }: { item: ActivityItem; controls: RowControls }) {
  const [confirming, setConfirming] = useState(false);
  const control = item.control;
  const allowed = !!control && controls.allowed(item);
  const queued = item.status === 'queued';
  const inLine = queued && item.position != null && !!item.queue;
  const last = item.queue ? controls.lengths[item.queue] - 1 : 0;
  const name = `${item.title}: ${item.scope}`;
  // Waiting and paused work keeps a still bar at how far it had got.
  const bar = item.status === 'running'
    || ((item.status === 'waiting' || item.status === 'paused') && item.percent != null);
  return (
    <li
      className="activity-row"
      data-job-state={item.status}
      title={item.hint}
    >
      <div className="activity-row-head">
        <span className="activity-row-title">{item.title}</span>
        <span className="activity-row-state">{stateText(item)}</span>
      </div>
      <div className="activity-row-scope">
        {item.href ? (
          <Link
            to={item.href}
            className="activity-row-link"
            aria-label={`Go to ${name}`}
            title="Go to it"
            onClick={controls.leave}
          >
            {item.scope}
          </Link>
        ) : (
          item.scope
        )}
      </div>
      {bar && (
        <div className="activity-row-bar" aria-hidden="true">
          <div
            className={`activity-row-fill${item.percent == null ? ' is-indeterminate' : ''}`}
            style={item.percent == null ? undefined : { width: `${item.percent}%` }}
          />
        </div>
      )}
      <div className="activity-row-foot">
        <div className="activity-row-detail">
          {item.detail}
          {item.remaining && <span className="activity-row-remaining"> · {item.remaining}</span>}
        </div>
        {item.transfer && controls.allowed(item) && (
          // Laid out by the row, whatever the Director page's styles do.
          <div className="activity-row-actions">
            <TransferControls job={item.transfer} busy={controls.busy}
              run={action => controls.run({ type: 'transfer', job: item.transfer!, action })} />
          </div>
        )}
        {item.pause && controls.allowed(item) && (
          <div className="activity-row-actions">
            <button
              type="button"
              aria-label={`${item.pause.paused ? 'Resume' : 'Pause'} ${name}`}
              title={item.pause.paused
                ? 'Start the paused work again'
                : 'Pause until you resume it or the server restarts'}
              disabled={controls.busy}
              // Pausing keeps the work, so it does not ask first.
              onClick={() => controls.run({ type: 'pause', pause: item.pause! })}
            >
              {item.pause.paused ? 'Resume' : 'Pause'}
            </button>
          </div>
        )}
        {allowed && control && (queued || (item.status === 'running' && item.stoppable)) && (
          <div className="activity-row-actions">
            {inLine && (
              <>
                {tierOf(item) === 'person' && (
                  <button
                    type="button"
                    aria-label={`Move ${name} to the top of the queue`}
                    title="Move to top"
                    disabled={controls.busy || item.position === 0}
                    onClick={() => controls.run({ type: 'move', control, position: 0 })}
                  >
                    <ArrowUpToLine size={10} aria-hidden="true" />
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`Run ${name} earlier`}
                  title="Earlier"
                  disabled={controls.busy || item.position === 0}
                  onClick={() => controls.run({ type: 'move', control, position: item.position! - 1 })}
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={`Run ${name} later`}
                  title="Later"
                  disabled={controls.busy || item.position === last}
                  onClick={() => controls.run({ type: 'move', control, position: item.position! + 1 })}
                >
                  ↓
                </button>
              </>
            )}
            {queued ? (
              <button
                type="button"
                className="activity-row-stop"
                aria-label={`Remove ${name} from the line`}
                title="Remove from the line"
                disabled={controls.busy}
                onClick={() => controls.run({ type: 'stop', control })}
              >
                ✕
              </button>
            ) : confirming ? (
              <>
                <button
                  type="button"
                  className="activity-row-stop"
                  disabled={controls.busy}
                  onClick={() => {
                    setConfirming(false);
                    controls.run({ type: 'stop', control });
                  }}
                >
                  Stop it
                </button>
                <button type="button" onClick={() => setConfirming(false)}>
                  Keep
                </button>
              </>
            ) : (
              <button
                type="button"
                className="activity-row-stop"
                aria-label={`Stop ${name}`}
                disabled={controls.busy}
                // Stopping running work loses it, so it asks once.
                onClick={() => setConfirming(true)}
              >
                Stop
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Background work in the header, kept small: the overall progress and how
 * many jobs run, wait in a line and wait their turn. Hover, focus or click
 * opens the queue: every catalog refresh, quality scan and stack build,
 * running or queued, on any database, and automatic work waiting while a
 * person's work runs, under two headings: the work a person started, and
 * automatic work. Each row links to what it works on. A scan's frame errors stay, on the
 * busy chip or as "Finished with errors", until dismissed.
 */
export default function ActivityChip() {
  const { items, summary, finished, scanErrors, dismissScanError } = useHeaderActivity();
  const scanError = scanErrors[scanErrors.length - 1];
  const access = useAccess();
  const queryClient = useQueryClient();
  const action = useMutation({
    mutationFn: runAction,
    // Each route returns its job's new status. The pages that show those
    // jobs read it at once; the header reads the job list again.
    onSuccess: (result, variables) => {
      if (variables.type === 'transfer') return;
      if (variables.type === 'pause') {
        // The Calibration page shows whether its jobs are paused.
        const { dbId, job } = variables.pause;
        if (job === 'flats') queryClient.invalidateQueries({ queryKey: flatMeasureKey(dbId) });
        if (job === 'sites') queryClient.invalidateQueries({ queryKey: frameSitesKey(dbId) });
        return;
      }
      if (!result || typeof result !== 'object') return;
      if (variables.control.kind === 'stack') {
        queryClient.setQueryData(STACK_ACTIVITY_QUERY_KEY, result);
      } else if (variables.control.kind === 'duplicates') {
        queryClient.setQueryData(duplicatesKey(variables.control.dbId), result);
      } else if (variables.control.kind === 'rejects') {
        queryClient.setQueryData(rejectRemovalKey(variables.control.dbId), result);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: STACK_ACTIVITY_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TRANSFER_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BACKUP_STATUS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TS_SYNC_QUERY_KEY });
      // The mutation stays pending, and the controls disabled, until the job
      // list is read again: a second click then acts on the new positions.
      return queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    },
  });
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const listId = useId();
  const open = (pinned || hovered) && (summary.count > 0 || scanErrors.length > 0);

  useEffect(() => () => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
  }, []);

  // A pinned list closes on a click elsewhere. Escape is handled on the
  // chip itself, so it never takes the key from the view underneath.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) {
        setPinned(false);
        setHovered(false);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  // Nothing left to show: forget the pin, so the next job starts closed.
  useEffect(() => {
    if (summary.count === 0 && scanErrors.length === 0) {
      setPinned(false);
      setHovered(false);
    }
  }, [summary.count, scanErrors.length]);

  // Always mounted, so a screen reader hears when work ends or fails.
  const announcement = finished
    ? finished.errors
      ? `Background jobs finished with errors. ${scanErrors.map((note) => note.message).join('. ')}`
      : 'Background jobs finished.'
    : scanError?.message
      ? `A quality scan finished with errors. ${scanError.message}`
      : '';
  const live = <span className="activity-live" aria-live="polite">{announcement}</span>;

  // Idle with errors held: the chip stays, as a button that lists them.
  const idleWithErrors = summary.count === 0 && scanErrors.length > 0;
  if (summary.count === 0 && !idleWithErrors && !finished) return live;

  if (summary.count === 0 && !idleWithErrors && finished) {
    return (
      <div className="activity-chip-slot">
        {live}
        <span className="header-button utility-button activity-chip is-finished">
          <span className="activity-chip-mark" aria-hidden="true">✓</span>
          <span className="activity-chip-text">Done</span>
        </span>
      </div>
    );
  }

  // Stack builds and WBPP runs are anyone's who can write.
  const controls: RowControls = {
    allowed: () => access.canWrite,
    run: (next) => {
      // A control was used, so the list stays open after the pointer leaves.
      setPinned(true);
      action.mutate(next);
    },
    busy: action.isPending,
    lengths: lineLengths(items),
    leave: () => {
      setPinned(false);
      setHovered(false);
    },
  };

  const percent = summary.percent == null ? null : Math.round(summary.percent);
  const { running, waiting, queued, paused, attention } = summary;
  const groups = groupActivity(items);
  const label = [
    percent != null ? `${percent}% done` : running > 0 ? 'Working' : '',
    running > 0 ? `${running} running` : '',
    queued > 0 ? `${queued} queued` : '',
    waiting > 0 ? `${waiting} waiting` : '',
    paused > 0 ? `${paused} paused` : '',
    attention > 0 ? countName(attention, 'attention') : '',
    scanError ? 'a quality scan had errors' : '',
  ].filter(Boolean).join(', ');

  const hover = (inside: boolean) => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (inside) setHovered(true);
    else closeTimer.current = window.setTimeout(() => setHovered(false), HOVER_CLOSE_MS);
  };

  return (
    <div
      ref={wrapper}
      className="activity-chip-slot"
      onPointerEnter={(event) => event.pointerType === 'mouse' && hover(true)}
      onPointerLeave={(event) => event.pointerType === 'mouse' && hover(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setPinned(false);
          setHovered(false);
        }
      }}
    >
      {live}
      <button
        type="button"
        className={`header-button utility-button activity-chip${scanError ? ' has-errors' : ''}${
          idleWithErrors ? ' is-finished' : ''
        }`}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={idleWithErrors ? 'Background jobs: finished with errors' : `Background jobs: ${label}`}
        title={idleWithErrors ? scanErrors.map((note) => note.message).join('\n') : undefined}
        // The first click keeps the list open after the pointer leaves; the
        // second closes it.
        onClick={() => {
          if (pinned) {
            setPinned(false);
            setHovered(false);
          } else {
            setPinned(true);
          }
        }}
        onFocus={() => hover(true)}
        onBlur={(event) => {
          if (!wrapper.current?.contains(event.relatedTarget as Node)) hover(false);
        }}
      >
        {scanError && <span className="activity-chip-mark" aria-hidden="true" title={scanError.message}>!</span>}
        {idleWithErrors ? (
          <span className="activity-chip-text">Finished with errors</span>
        ) : (
          <>
            <ProgressRing percent={summary.percent} idle={running === 0} />
            {percent != null && <span className="activity-chip-percent">{percent}%</span>}
            <JobCounts summary={summary} />
          </>
        )}
      </button>
      {open && (
        <div id={listId} className="activity-popover" role="region" aria-label="Background jobs">
          <div className="activity-popover-head">
            <h2 className="activity-popover-title">Background jobs</h2>
            <span className="activity-popover-counts">
              {idleWithErrors ? 'Nothing running' : <JobCounts summary={summary} />}
            </span>
          </div>
          {scanErrors.map((note) => (
            <ScanErrorRow
              key={note.dbId}
              note={note}
              onDismiss={dismissScanError}
              onLeave={controls.leave}
            />
          ))}
          {action.isError && (
            <p className="activity-error" role="alert">{(action.error as Error).message}</p>
          )}
          {groups.map((group) => (
            <div
              key={group.tier}
              role="group"
              className="activity-group"
              data-tier={group.tier}
              aria-labelledby={`${listId}-${group.tier}`}
            >
              <div className="activity-group-head">
                <h3 id={`${listId}-${group.tier}`} className="activity-group-title">{group.title}</h3>
                <JobCounts summary={group.summary} />
              </div>
              <ul className="activity-list">
                {group.items.map((item) => <ActivityRow key={item.key} item={item} controls={controls} />)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
